"use client";

import BlokWrap from "@/components/atoms/BlokWrap";
import StoryblokComponent from "@/lib/storyblok/storyblokSetup/StoryblokComponent/StoryblokComponent";
import type { FeaturedStoryblok } from "@/types/storyblok-blok-types";
import { type SbBlokData, storyblokEditable } from "@storyblok/react/rsc";
import { useEffect, useRef } from "react";
import styles from "./Featured.module.css";

export interface FeaturedBlokProps {
  blok: FeaturedStoryblok;
  preview: boolean;
}

export const FeaturedBlok = ({ blok }: FeaturedBlokProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const isMobileSlider = Boolean(blok.sliderOnMobile);

  useEffect(() => {
    if (!isMobileSlider || !scrollRef.current || !scrollbarRef.current) return;

    const controller = new AbortController();

    const handleScroll = () => {
      const element = scrollRef.current;
      const scrollbar = scrollbarRef.current;
      if (!element || !scrollbar) return;

      const scrollWidth = element.scrollWidth;
      const clientWidth = element.clientWidth;
      const scrollLeft = element.scrollLeft;

      const thumbWidth = (clientWidth / scrollWidth) * 100;

      const maxScroll = scrollWidth - clientWidth;
      const scrollProgress =
        maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - thumbWidth) : 0;

      scrollbar.style.setProperty("--thumb-width", `${thumbWidth}%`);
      scrollbar.style.setProperty("--thumb-position", `${scrollProgress}%`);
    };

    const element = scrollRef.current;
    element.addEventListener("scroll", handleScroll, {
      signal: controller.signal,
    });
    handleScroll();

    return () => {
      controller.abort();
    };
  }, [isMobileSlider]);

  return (
    <BlokWrap animated="" {...storyblokEditable(blok as unknown as SbBlokData)}>
      <p className={styles.heading}>{blok.heading}</p>
      <div
        ref={scrollRef}
        className={styles.items}
        data-mobile-slider={isMobileSlider}
      >
        {blok.items?.map((item, i) => (
          <StoryblokComponent key={i} blok={item} />
        ))}
      </div>
      {isMobileSlider && (
        <div className={styles.scrollbarWrapper}>
          <div className={styles.scrollbar} ref={scrollbarRef}>
            <div className={styles.scrollbarDrag} />
          </div>
        </div>
      )}
    </BlokWrap>
  );
};
